import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/auth';
import { AngularFirestore, Query } from '@angular/fire/firestore';
import { SurveyAnswer } from '@app/interface/surveyAnswer.model';
import * as FileSaver from 'file-saver';
import { Observable, of } from 'rxjs';
import { debounce, delay, map, mergeMap, switchMap, take } from 'rxjs/operators';
import * as XLSX from 'xlsx';
import { FirebaseAuthService } from './firebaseauth.service';
import { StudiesService } from './studies.service';

@Injectable({
  providedIn: 'root',
})
export class SurveyAnswerService {
  readonly EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
  readonly EXCEL_EXTENSION = '.xlsx';

  constructor(
    private store: AngularFirestore,
    private afAuth: AngularFireAuth,
    private authService: FirebaseAuthService,
    private studiesService: StudiesService
  ) {
    this.getUserByStudyId();
  }

  public exportAsExcelFile(table: HTMLElement, excelFileName: string): void {
    const worksheet: XLSX.WorkSheet = XLSX.utils.table_to_sheet(table);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, worksheet, excelFileName);
    XLSX.writeFile(wb, excelFileName + this.EXCEL_EXTENSION);
  }

  private saveAsExcelFile(buffer: any, fileName: string): void {
    const data: Blob = new Blob([buffer], {
      type: this.EXCEL_TYPE,
    });
    FileSaver.saveAs(data, fileName + '_export_' + new Date().getTime() + this.EXCEL_EXTENSION);
  }

  public getSurveyBySurveyStudyId(surveyId: string, studyId: string): Observable<any> {
    return this.afAuth.authState.pipe(
      mergeMap((user: firebase.default.User) => {
        if (user) {
          return this.store
            .collection<SurveyAnswer>('survey-answers', (ref) => {
              let query: Query = ref;
              return query
                .where('completedPercentage', '>', 0)
                .where('studyId', '==', studyId)
                .where('surveyId', '==', surveyId);
            })
            .valueChanges()
            .pipe(take(1));
        } else {
          return of([]);
        }
      })
    );
  }

  public getSurveysAndAnswersByStudyId(userId?: string): Observable<any> {
    let studyId = this.studiesService.getStudyId();
    return this.afAuth.authState.pipe(
      switchMap((user: firebase.default.User) => {
        if (user && studyId) {
          return this.store
            .collection('survey-answers', (ref) => {
              let query: Query = ref;
              return query.where('studyId', '==', studyId).where('userId', '==', userId).orderBy('endAt', 'desc');
            })
            .valueChanges();
        } else {
          return of([]);
        }
      }),
      take(1)
    );
  }

  public getCognitivesAndResultsByStudyId(userId?: string): Observable<any> {
    let studyId = this.studiesService.getStudyId();
    return this.afAuth.authState.pipe(
      switchMap((user: firebase.default.User) => {
        if (user && studyId) {
          return this.store
            .collection('cognitive-results', (ref) => {
              let query: Query = ref;
              return query.where('studyId', '==', studyId).where('userId', '==', userId).orderBy('endAt');
            })
            .valueChanges();
        } else {
          return of([]);
        }
      }),
      take(1)
    );
  }

  public getUserByStudyId(): Observable<any> {
    let studyId = this.studiesService.getStudyId();
    return this.afAuth.authState.pipe(
      switchMap((user: firebase.default.User) => {
        if (user && studyId) {
          return this.store
            .collection('survey-answers', (ref) => {
              let query: Query = ref;
              return query.where('studyId', '==', studyId);
            })
            .valueChanges();
        } else {
          return of([]);
        }
      }),
      take(1)
    );
  }

  public async getSurveyAnswerBySId(surveyId: string, studyId: string): Promise<any> {
    let surveyDoc = await this.store
      .collection<SurveyAnswer>('survey-answers')
      .ref.where('completedPercentage', '>', 0)
      .where('studyId', '==', studyId)
      .where('surveyId', '==', surveyId)
      .get();
    if (surveyDoc.docs.length === 0) {
      return [];
    }
    return await this.filterData(surveyDoc.docs);
  }

  private async filterData(answers: any[]): Promise<any> {
    return await new Promise(async (resolve, reject) => {
      let data = [] as any[];
      for (let index = 0; index < answers.length; index++) {
        const doc = answers[index];
        let element = doc.data();
        let userDoc = await element.userRef.get();
        let user = userDoc.data();
        element.email = user.email;
        data.push(element);
      }
      resolve(data);
    });
  }
}
